// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Shield, Loader2, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { ShortDescriptionField } from "./ShortDescriptionField";
import type { EventData, ConstraintTemplate, EventConstraintOffering } from "@/types/events";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
}

const TYPE_LABEL: Record<string, string> = {
  team_blackout: "Blackout",
  must_play_before: "Latest",
  must_play_after: "Earliest",
  coach_conflict: "Coach",
  venue_preference: "Venue",
  game_count_limit: "Games",
  divisional_ordering: "Matchup",
  do_not_play: "Do Not Play",
  must_play_same_time: "Same Time",
};

const TYPE_GROUP: Record<string, string> = {
  team_blackout: "Schedule Windows",
  must_play_before: "Schedule Windows",
  must_play_after: "Schedule Windows",
  coach_conflict: "Coach Conflicts",
  venue_preference: "Venue",
  game_count_limit: "Game Count",
  divisional_ordering: "Matchups",
  do_not_play: "Matchups",
  must_play_same_time: "Matchups",
};

const GROUP_ORDER = ["Schedule Windows", "Coach Conflicts", "Venue", "Game Count", "Matchups"];


const SEVERITY_OPTS = [
  { v: "hard", label: "Hard rule", chip: "bg-red-100 text-red-700 border-red-200" },
  { v: "soft", label: "Soft preference", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  { v: "preference", label: "Preference", chip: "bg-gray-100 text-gray-700 border-gray-200" },
];

function severityChip(sev: string) {
  const o = SEVERITY_OPTS.find((s) => s.v === sev) ?? SEVERITY_OPTS[1];
  return <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${o.chip}`}>{o.label}</span>;
}

export function ConstraintsSection({ event, updateEvent }: Props) {
  const [templates, setTemplates] = useState<ConstraintTemplate[]>([]);
  const [offerings, setOfferings] = useState<EventConstraintOffering[]>([]);
  const [loading, setLoading] = useState(false);

  const sportId = event.sport_id || null;
  const orgId = event.organization_id || null;
  const eventId = event.id || null;

  const enabled = !!event.accept_special_requests;

  // Load templates
  useEffect(() => {
    if (!sportId) {
      setTemplates([]);
      return;
    }
    (async () => {
      setLoading(true);
      let q = (supabase as any)
        .from("constraint_templates")
        .select("*")
        .eq("is_active", true)
        .or(`sport_id.eq.${sportId},sport_id.is.null`);
      if (orgId) q = q.or(`organization_id.eq.${orgId},organization_id.is.null`);
      const { data } = await q.order("display_order");
      setTemplates(data ?? []);
      setLoading(false);
    })();
  }, [sportId, orgId]);

  // Load offerings
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      const { data } = await (supabase as any)
        .from("event_constraint_offerings")
        .select("*")
        .eq("event_id", eventId);
      setOfferings(data ?? []);
    })();
  }, [eventId]);

  const offeringByTemplate = useMemo(() => {
    const m: Record<string, EventConstraintOffering> = {};
    for (const o of offerings) m[o.template_id] = o;
    return m;
  }, [offerings]);

  const upsertOffering = async (tplId: string, patch: Partial<EventConstraintOffering>) => {
    if (!eventId) return;
    const existing = offeringByTemplate[tplId];
    const row = {
      event_id: eventId,
      template_id: tplId,
      override_fee: existing?.override_fee ?? null,
      override_severity: existing?.override_severity ?? null,
      override_requires_review: existing?.override_requires_review ?? null,
      is_active: existing?.is_active ?? true,
      display_order: existing?.display_order ?? 0,
      ...patch,
    };
    const { data, error } = await (supabase as any)
      .from("event_constraint_offerings")
      .upsert(row, { onConflict: "event_id,template_id" })
      .select()
      .single();
    if (error) {
      console.error("offering upsert", error);
      return;
    }
    setOfferings((prev) => {
      const without = prev.filter((o) => o.template_id !== tplId);
      return [...without, data];
    });
  };

  const toggleTemplate = (tpl: ConstraintTemplate, on: boolean) => {
    upsertOffering(tpl.id, { is_active: on });
  };

  const resetOverrides = (tplId: string) =>
    upsertOffering(tplId, {
      override_fee: null,
      override_severity: null,
      override_requires_review: null,
    });

  const bulkSetAll = async (on: boolean) => {
    for (const t of templates) await upsertOffering(t.id, { is_active: on });
  };
  const resetAll = async () => {
    for (const t of templates) await resetOverrides(t.id);
  };

  // Group templates
  const grouped = useMemo(() => {
    const g: Record<string, ConstraintTemplate[]> = {};
    for (const t of templates) {
      const grp = TYPE_GROUP[t.constraint_type] ?? "Other";
      (g[grp] ??= []).push(t);
    }
    // Never silently drop a template whose type isn't in GROUP_ORDER.
    const extras = Object.keys(g).filter((k) => !GROUP_ORDER.includes(k)).sort();
    return [...GROUP_ORDER, ...extras]
      .filter((k) => g[k]?.length)
      .map((k) => ({ group: k, items: g[k] }));
  }, [templates]);




  return (
    <div className="space-y-6">
      <SubSection title="Constraints">
        <ShortDescriptionField
          value={(event as any).schedule_requests_short_description}
          onChange={(v) => updateEvent({ schedule_requests_short_description: v } as any)}
        />
        <p className="text-xs text-muted-foreground -mt-2 mb-3">
          Pre-approved scheduling constraints teams can request at registration. The scheduler
          honors these automatically — no free-text requests.
        </p>

        <label className="flex items-center justify-between p-3 border border-border rounded-lg">
          <span className="text-sm font-medium">Allow teams to request constraints</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => updateEvent({ accept_special_requests: e.target.checked })}
            className="rounded"
          />
        </label>

        {!enabled && (
          <p className="text-xs text-muted-foreground mt-2">
            Teams cannot request constraints. Scheduler will use defaults only.
          </p>
        )}
      </SubSection>

      {enabled && (
        <SubSection title="Choose which constraints apply to this event">
          <div className="flex items-start justify-between gap-4 -mt-2 mb-3">
            <p className="text-xs text-muted-foreground flex-1">
              Templates are managed in the Constraints catalog. Add new templates there.
              Available templates are filtered by this event's sport.
            </p>
            <Link
              to="/admin-dashboard/constraints"
              className="text-xs text-primary hover:underline whitespace-nowrap inline-flex items-center gap-1"
            >
              Manage constraint catalog <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          {!sportId ? (
            <div className="p-6 border border-dashed border-border rounded-lg text-center">
              <Shield className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Select a sport in Basic Info to choose constraints.</p>
            </div>
          ) : loading ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : templates.length === 0 ? (
            <div className="p-6 border border-dashed border-border rounded-lg text-center">
              <p className="text-sm font-medium mb-1">No constraint templates for this sport yet</p>
              <Link to="/admin-dashboard/constraints" className="text-xs text-primary hover:underline">
                Create one in the catalog →
              </Link>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={() => bulkSetAll(true)} className="text-xs px-3 py-1.5 border border-border rounded hover:border-primary">
                  Enable all
                </button>
                <button onClick={() => bulkSetAll(false)} className="text-xs px-3 py-1.5 border border-border rounded hover:border-primary">
                  Disable all
                </button>
                <button onClick={resetAll} className="text-xs px-3 py-1.5 border border-border rounded hover:border-primary">
                  Reset all overrides to defaults
                </button>
              </div>

              <div className="space-y-5">
                {grouped.map(({ group, items }) => (
                  <div key={group}>
                    <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group}</h5>
                    <div className="space-y-2">
                      {items.map((tpl) => {
                        const off = offeringByTemplate[tpl.id];
                        const checked = !!off?.is_active;
                        const fee = off?.override_fee ?? null;
                        const sev = off?.override_severity ?? null;
                        const review = off?.override_requires_review ?? null;
                        const hasOverride = fee !== null || sev !== null || review !== null;
                        return (
                          <div
                            key={tpl.id}
                            className={`border border-border rounded-lg p-3 transition-colors ${checked ? "bg-card" : "bg-muted/30"}`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleTemplate(tpl, e.target.checked)}
                                className="mt-1 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className={`text-sm font-medium ${!checked ? "text-muted-foreground" : ""}`}>
                                    {tpl.label}
                                  </span>
                                  <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-secondary text-foreground border-border">
                                    {TYPE_LABEL[tpl.constraint_type] ?? tpl.constraint_type}
                                  </span>
                                  {severityChip(sev ?? tpl.default_severity)}
                                </div>
                                {tpl.description && (
                                  <p className={`text-xs mt-1 ${!checked ? "text-muted-foreground/70" : "text-muted-foreground"}`}>
                                    {tpl.description}
                                  </p>
                                )}

                                {checked && (
                                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                      <FieldLabel>Fee override</FieldLabel>
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                                        <input
                                          type="number"
                                          value={fee ?? ""}
                                          placeholder={String(tpl.default_fee ?? 0)}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            upsertOffering(tpl.id, {
                                              override_fee: v === "" ? null : parseFloat(v) || 0,
                                            });
                                          }}
                                          className={`${inputClass} pl-5`}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <FieldLabel>Severity override</FieldLabel>
                                      <select
                                        value={sev ?? ""}
                                        onChange={(e) =>
                                          upsertOffering(tpl.id, {
                                            override_severity: e.target.value === "" ? null : (e.target.value as any),
                                          })
                                        }
                                        className={inputClass}
                                      >
                                        <option value="">Default ({SEVERITY_OPTS.find((s) => s.v === tpl.default_severity)?.label})</option>
                                        {SEVERITY_OPTS.map((s) => (
                                          <option key={s.v} value={s.v}>{s.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <FieldLabel>Requires review</FieldLabel>
                                      <select
                                        value={review === null ? "" : review ? "yes" : "no"}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          upsertOffering(tpl.id, {
                                            override_requires_review: v === "" ? null : v === "yes",
                                          });
                                        }}
                                        className={inputClass}
                                      >
                                        <option value="">Default ({tpl.requires_review ? "Yes" : "No"})</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                      </select>
                                    </div>

                                    {hasOverride && (
                                      <button
                                        onClick={() => resetOverrides(tpl.id)}
                                        className="sm:col-span-3 inline-flex items-center gap-1 text-xs text-primary hover:underline w-fit"
                                      >
                                        <RotateCcw className="w-3 h-3" /> Reset to defaults
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SubSection>
      )}
    </div>
  );
}
